/**
 * Email Events Page Component
 *
 * Main page for managing email event configurations.
 * Displays list of events and provides create/edit functionality via modals.
 */

import { Card, Text, Button, Group } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconPlus } from "@tabler/icons-react";
import { useCallback, useState } from "react";
import type { EmailEventType } from "../../../../../../../types/email-event.d";
import { useEmailEvents } from "../../hooks/useEmailEvents";
import type { EmailEventWithId, EmailEventFormData } from "../../schema/email-event.types";
import { EmailEventForm } from "../email-event-form";
import { EmailEventsList } from "../email-events-list";

/**
 * Props for EmailEventsPage
 */
export interface EmailEventsPageProperties {
  /** Licenciada ID for email events */
  licenciadaId: string;
}

/**
 * EmailEventsPage Component
 * @param root0
 * @param root0.licenciadaId
 * @example
 * <EmailEventsPage licenciadaId="lic123" />
 */
export function EmailEventsPage({ licenciadaId }: EmailEventsPageProperties) {
  const { events, loading, createEvent, updateEvent, deleteEvent, toggleEvent } = useEmailEvents(licenciadaId);
  const [formLoading, setFormLoading] = useState(false);
  const handleFormCancel = useCallback(() => {
    modals.close("email-event-form");
  }, []);

  const handleCreateSubmit = useCallback(
    async (data: EmailEventFormData) => {
      setFormLoading(true);
      try {
        await createEvent(data);
        modals.close("email-event-form");
      } finally {
        setFormLoading(false);
      }
    },
    [createEvent]
  );

  const handleEditSubmit = useCallback(
    async (eventType: EmailEventType, data: EmailEventFormData) => {
      setFormLoading(true);
      try {
        await updateEvent(eventType, data);
        modals.close("email-event-form");
      } finally {
        setFormLoading(false);
      }
    },
    [updateEvent]
  );

  /**
   * Open modal to create a new email event
   */
  const handleCreate = () => {
    modals.open({
      modalId: "email-event-form",
      title: "Criar Evento de Email",
      size: "xl",
      children: (
        <EmailEventForm
          licenciadaId={licenciadaId}
          onSubmit={handleCreateSubmit}
          onCancel={handleFormCancel}
          loading={formLoading}
        />
      )
    });
  };

  /**
   * Open modal to edit an existing email event
   * @param event
   */
  const handleEdit = (event: EmailEventWithId) => {
    const handleSubmit = (data: EmailEventFormData) => {
      void handleEditSubmit(event.event_type, data);
    };

    modals.open({
      modalId: "email-event-form",
      title: "Editar Evento de Email",
      size: "xl",
      children: (
        <EmailEventForm
          licenciadaId={licenciadaId}
          initialValues={event}
          onSubmit={handleSubmit}
          onCancel={handleFormCancel}
          loading={formLoading}
        />
      )
    });
  };

  /**
   * Handle event deletion
   * @param eventType
   */
  const handleDelete = async (eventType: EmailEventType) => {
    await deleteEvent(eventType);
  };

  /**
   * Handle event toggle (enable/disable)
   * @param eventType
   * @param enabled
   */
  const handleToggle = async (eventType: EmailEventType, enabled: boolean) => {
    await toggleEvent(eventType, enabled);
  };

  return (
    <>
      <Card
        withBorder
        shadow="md"
        styles={{
          section: {
            overflow: "auto"
          }
        }}
      >
        {/* Header */}
        <Card.Section px="md" py="xs" withBorder>
          <Group justify="space-between">
            <Text fw={500} fz="lg">
              Eventos de Email
            </Text>
            <Button leftSection={<IconPlus size="1rem" />} onClick={handleCreate} size="sm">
              Novo Evento
            </Button>
          </Group>
        </Card.Section>

        {/* Email Events List */}
        <Card.Section>
          <EmailEventsList
            events={events}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggle={handleToggle}
            onCreate={handleCreate}
          />
        </Card.Section>
      </Card>
    </>
  );
}

EmailEventsPage.displayName = "EmailEventsPage";
